import { Navigate, Outlet, useLocation } from 'react-router';

import { LinkButton } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/StateMessage';
import type { Permission } from '@/constants/permissions';
import { ROUTES } from '@/constants/routes';

import { useCurrentUser, usePermissions, useSessionToken } from '../../hooks/useSession';
import { FullPageError, FullPageLoading, MemberAppNotice } from './FullPageState';
import styles from './Guards.module.css';

/**
 * Layout route for everything behind sign-in. It loads the profile once, sends a signed-out
 * visitor to the login page (remembering where they were going), turns away member logins
 * (members use the mobile app; this website is for staff), and holds anyone with a pending
 * forced password change on the change-password screen.
 */
export function RequireAuth() {
  const token = useSessionToken();
  const location = useLocation();
  const { data: user, isPending, isError, error, refetch } = useCurrentUser();

  if (token === null) {
    return (
      <Navigate
        to={ROUTES.LOGIN}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  if (isPending) return <FullPageLoading />;
  if (isError) {
    return (
      <FullPageError
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  if (user.member !== null) return <MemberAppNotice />;
  if (user.mustChangePassword && location.pathname !== ROUTES.CHANGE_PASSWORD) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }
  return <Outlet />;
}

/** Layout route for the login page: a signed-in visitor goes straight to the app. */
export function RequireGuest() {
  const token = useSessionToken();
  if (token !== null) return <Navigate to={ROUTES.HOME} replace />;
  return <Outlet />;
}

/** `/` — the dashboard. Only staff get past `RequireAuth`. */
export function HomeRedirect() {
  return <Navigate to={ROUTES.DASHBOARD} replace />;
}

/** Shown in place of a screen the user's role does not include. */
export function NoAccess() {
  return (
    <div className={styles.noAccess}>
      <EmptyState
        icon="lock"
        title="You don't have access to this screen"
        description="Your role does not include it. Ask the gym admin if you need it."
        action={
          <LinkButton to={ROUTES.HOME} variant="secondary" icon="home">
            Go to home
          </LinkButton>
        }
      />
    </div>
  );
}

/** Layout route that renders its screens only for users holding `permission`. */
export function RequirePermission({ permission }: { permission: Permission }) {
  const { can } = usePermissions();
  if (!can(permission)) return <NoAccess />;
  return <Outlet />;
}
